/**
 * slot-reading — 규칙 읽기 상자의 자리 표(VOCABULARY "규칙 읽기" · "채운 자리").
 * 제품 등록 폼과 요청 상세의 승격 폼이 같이 쓴다(2026-10-26 승격도 규칙이). 자리 칸의 액션은 SLOT_FILL_ACTION, data-key = 역할.
 */
import { E_DESC_TOKEN_ROLES, TOKEN_ROLE_WORD } from '../api/client';
import type { DescriptionReading, EDescTokenRole, SlotFill, SlotReading } from '../api/client';
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/cells';

export const SLOT_FILL_ACTION = 'slot-fill';   // 규칙 읽기 상자의 자리 칸(채운 자리)

/** 포커스된 자리 칸 — 상자를 다시 그린 뒤 되돌릴 역할 · 커서 */
export type FocusedSlot = { role: EDescTokenRole; caret: number };

/** 자리 줄들 — 역할 한글(필수면 *) · 입력 칸(읽은 말 또는 채운 글). 필수인데 비면 붉게 */
export function renderSlotTable(reading: DescriptionReading, fills: SlotFill[]): string {
  const rows = reading.slots.map((slot) => slotRowOf(slot, fills)).join('');
  return `<table class="pv-table pc-reading-table"><tbody>${rows}</tbody></table>`;
}

/** 필수인데 빈 자리 — 읽힌 말도 채운 글도 없음 */
export function missingSlotRolesOf(reading: DescriptionReading, fills: SlotFill[]): EDescTokenRole[] {
  const filled = trimmedFillsOf(fills).map((fill) => fill.role);
  return reading.slots
    .filter((slot) => slot.required && slot.text === null && !filled.includes(slot.role))
    .map((slot) => slot.role);
}

/** 자리 하나의 채운 글 — 친 그대로 둔다(치는 중 공백 보존). 그 역할 줄을 바꾸고, 다 지우면 줄째 뺀다 */
export function withSlotFill(fills: SlotFill[], role: EDescTokenRole, text: string): SlotFill[] {
  const others = fills.filter((fill) => fill.role !== role);
  return text === '' ? others : [...others, { role, text }];
}

/** 보낼 채운 자리 — trim 하고 빈 글은 뺀다. 읽기 요청 · 저장 둘 다 */
export function trimmedFillsOf(fills: SlotFill[]): SlotFill[] {
  return fills
    .map((fill) => ({ role: fill.role, text: fill.text.trim() }))
    .filter((fill) => fill.text !== '');
}

export function slotRoleOf(key: string | undefined): EDescTokenRole {
  const role = E_DESC_TOKEN_ROLES.find((candidate) => candidate === key);
  assert(role !== undefined, `자리 칸의 data-key 는 E_DESC_TOKEN_ROLES 중 하나 — slotRowOf 가 그렸다: ${String(key)}`);
  return role;
}

/** 상자 안 자리 칸이 포커스면 그 역할 · 커서. 아니면 null */
export function focusedSlotOrNull(): FocusedSlot | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLInputElement) || active.dataset.action !== SLOT_FILL_ACTION) return null;
  const caret = active.selectionStart === null ? active.value.length : active.selectionStart;
  return { role: slotRoleOf(active.dataset.key), caret };
}

/** 다시 그린 상자(regionSelector)에서 그 역할의 칸에 포커스 · 커서를 되돌린다 — 칸이 없어졌으면(규칙이 바뀜) 그냥 둔다 */
export function refocusSlot(root: HTMLElement | null, regionSelector: string, focused: FocusedSlot): void {
  const input = root === null ? null : root.querySelector<HTMLInputElement>(`${regionSelector} [data-action="${SLOT_FILL_ACTION}"][data-key="${focused.role}"]`);
  if (input === null) return;
  input.focus();
  input.setSelectionRange(focused.caret, focused.caret);
}

/** 자리 한 줄 — 역할 한글(필수면 *) · 입력 칸. 모든 자리가 칸이다(현민 2026-10-28 — 읽힌 자리도 고친다).
 *  값 = 채운 글, 안 채웠으면 원문에서 읽은 말. 채운 자리는 그 역할의 원문 말을 덮어쓴다(서버 tokensWithFills) — 칸을 비우면 읽은 말로 돌아간다.
 *  채운 자리엔 "직접" 표시. 필수인데 읽힌 말도 채운 글도 없으면 붉게 */
function slotRowOf(slot: SlotReading, fills: SlotFill[]): string {
  const fill = fills.find((item) => item.role === slot.role);
  const value = fill !== undefined ? fill.text : slot.text === null ? '' : slot.text;
  const input = `<input type="text" data-action="${SLOT_FILL_ACTION}" data-key="${slot.role}" value="${escapeHtml(value)}" placeholder="비어 있음 — 직접 입력" autocomplete="off">`;
  const mark = fill === undefined ? '' : '<small class="pc-slot-mark" title="직접 넣은 값 — 칸을 비우면 디스크립션에서 읽은 값으로 돌아갑니다">직접</small>';
  const bMissing = slot.required && slot.text === null && fill === undefined;
  return `<tr class="${bMissing ? 'is-missing' : ''}"><td>${TOKEN_ROLE_WORD[slot.role]}${slot.required ? ' *' : ''}</td><td>${input}${mark}</td></tr>`;
}
